import { useEffect, useRef, useState } from 'react';
import { useAppStore, roleOf, nameOf } from '../store/useAppStore.js';
import { useLobby } from '../lobby/LobbyContext.js';
import { PhoneIncomingIcon, HangupIcon, StarIcon, ClockIcon, LightbulbIcon } from '../icons.js';

// The incoming invite auto-declines if the guide doesn't answer in time, so a
// missed call returns the caller to the lobby instead of ringing forever.
const AUTO_DECLINE_SECONDS = 30;

export function IncomingView() {
  // The caller's auth uuid — a key. Everything shown below is resolved from it.
  const incomingFrom = useAppStore((s) => s.incomingFrom);
  const incomingFromName = useAppStore((s) => s.incomingFromName);
  const incomingDurationMinutes = useAppStore((s) => s.incomingDurationMinutes);
  const incomingTopic = useAppStore((s) => s.incomingTopic);
  const users = useAppStore((s) => s.users);
  const { acceptIncoming, declineIncoming } = useLobby();

  // The name off the caller's LiveKit token wins over the roster: a learner who
  // registered after this guide's page loaded is not in the roster at all.
  const callerName = incomingFromName ?? nameOf(users, incomingFrom);
  const initial = callerName?.slice(0, 1).toUpperCase() ?? '?';

  // Caller context — only data we actually have: what they want to work on,
  // their role, and rating if they've been rated. Level and prior-session
  // history aren't tracked yet, so those chips are intentionally absent.
  const callerRole = incomingFrom ? roleOf(users, incomingFrom) : null;
  const summary = users.find((u) => u.id === incomingFrom);
  const ratingCount = summary?.ratingCount ?? 0;
  const ratingAvg = summary?.ratingAvg ?? null;

  // Auto-decline countdown. One timer at a time; declines at zero. The view
  // remounts per invite, so the count starts fresh each call.
  const [secondsLeft, setSecondsLeft] = useState(AUTO_DECLINE_SECONDS);
  const declineRef = useRef(declineIncoming);
  declineRef.current = declineIncoming;
  useEffect(() => {
    if (secondsLeft <= 0) { declineRef.current(); return; }
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  return (
    <div className="flex items-center justify-center min-h-[60vh] animate-fade-up">
      <div className="card max-w-md w-full p-8 sm:p-10 text-center has-grain">
        <div className="flex items-center justify-center gap-2 text-eyebrow !text-ink-900 dark:!text-brand-bright mb-6">
          <span className="w-2 h-2 rounded-full bg-brand dark:bg-brand-bright animate-blink" />
          Incoming session
        </div>

        {/* Pulsing avatar — gold halo via the ring-pulse keyframe. */}
        <div
          className="mx-auto w-28 h-28 rounded-full mb-5 flex items-center justify-center
                     bg-brand dark:bg-brand-bright text-ink-900 font-display text-5xl font-semibold
                     animate-ring-pulse"
        >
          {initial}
        </div>

        <h2 className="h-card text-ink-900 dark:text-ink-100 mb-1">
          {callerName ?? 'Someone'}
        </h2>
        <p className="text-ink-600 dark:text-[#b6b2c4]">would like to start a session with you.</p>

        {(incomingTopic || incomingDurationMinutes != null || callerRole || (ratingCount > 0 && ratingAvg != null)) ? (
          <div className="mt-5 flex justify-center flex-wrap gap-2">
            {incomingTopic ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.78rem] font-medium
                               bg-brand/15 text-ink-900 border border-brand/30
                               dark:bg-brand-bright/10 dark:text-brand-bright dark:border-brand-bright/25">
                <LightbulbIcon width={13} height={13} />
                {incomingTopic}
              </span>
            ) : null}
            {incomingDurationMinutes != null ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.78rem] font-medium
                               bg-brand/15 text-ink-900 border border-brand/30
                               dark:bg-brand-bright/10 dark:text-brand-bright dark:border-brand-bright/25">
                <ClockIcon width={13} height={13} />
                Wants a {incomingDurationMinutes}-min session
              </span>
            ) : null}
            {callerRole ? (
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-[0.78rem] capitalize
                               bg-ink-100 text-ink-700 dark:bg-canvas-dark dark:text-ink-200
                               border border-ink-200 dark:border-white/10">
                {callerRole}
              </span>
            ) : null}
            {ratingCount > 0 && ratingAvg != null ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.78rem]
                               bg-emerald-50 text-emerald-700 dark:bg-status-successBright/10 dark:text-status-successBright
                               border border-emerald-200 dark:border-status-successBright/25">
                <StarIcon filled width={13} height={13} />
                {ratingAvg.toFixed(1)} ({ratingCount})
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            className="btn-primary flex-1 justify-center"
            onClick={() => { void acceptIncoming(); }}
          >
            <PhoneIncomingIcon /> Accept
          </button>
          <button
            type="button"
            className="btn-ghost flex-1 justify-center
                       hover:!text-status-error hover:!border-status-error
                       dark:hover:!text-status-errorBright dark:hover:!border-status-errorBright"
            onClick={declineIncoming}
          >
            <HangupIcon /> Decline
          </button>
        </div>

        <p className="mt-4 text-[0.82rem] text-ink-500 dark:text-[#b6b2c4]">
          Auto-declines in{' '}
          <span className="font-semibold tabular-nums text-ink-900 dark:text-brand-bright">
            {secondsLeft}s
          </span>
        </p>
      </div>
    </div>
  );
}
